import { useEffect, useState, useCallback } from "react";
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Frame } from "lucide-react";
import { resolveImage } from "@/lib/api";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import WallView from "./WallView";

/**
 * Full-screen lightbox: shows the FULL artwork (object-contain), click to toggle zoom,
 * keyboard arrows + escape, prev/next when multiple images.
 */
export default function Lightbox({ images = [], alt = "", initialIndex = 0, onClose }) {
  const safe = (images || []).filter(Boolean);
  const [idx, setIdx] = useState(initialIndex);
  const [zoomed, setZoomed] = useState(false);
  const [wallOpen, setWallOpen] = useState(false);
  const { wall_view_enabled } = useSiteSettings();

  const prev = useCallback(() => setIdx(i => (i - 1 + safe.length) % safe.length), [safe.length]);
  const next = useCallback(() => setIdx(i => (i + 1) % safe.length), [safe.length]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose, prev, next]);

  if (safe.length === 0) return null;
  const src = resolveImage(safe[idx]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[999] bg-black/95 backdrop-blur-sm flex items-center justify-center"
      data-testid="artwork-lightbox"
      onClick={onClose}
    >
      {/* Top controls */}
      <div className="absolute top-4 right-4 flex gap-2 z-10">
        {wall_view_enabled && (
          <button
            onClick={(e) => { e.stopPropagation(); setWallOpen(true); }}
            className="px-3 h-10 bg-white/5 hover:bg-[#C9A055] hover:text-black border border-white/20 text-[#F4F1EA] flex items-center gap-1.5 font-ui text-[10px] tracking-[0.28em] uppercase"
            aria-label="View on a wall"
            data-testid="lightbox-wallview-btn"
          >
            <Frame size={14} /> Wall View
          </button>
        )}
        <button
          onClick={(e) => { e.stopPropagation(); setZoomed(z => !z); }}
          className="w-10 h-10 bg-white/5 hover:bg-[#C9A055] hover:text-black border border-white/20 text-[#F4F1EA] flex items-center justify-center"
          aria-label={zoomed ? "Zoom out" : "Zoom in"}
          data-testid="lightbox-zoom-toggle"
        >
          {zoomed ? <ZoomOut size={16} /> : <ZoomIn size={16} />}
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onClose?.(); }}
          className="w-10 h-10 bg-white/5 hover:bg-[#C9A055] hover:text-black border border-white/20 text-[#F4F1EA] flex items-center justify-center"
          aria-label="Close"
          data-testid="lightbox-close"
        >
          <X size={16} />
        </button>
      </div>

      {/* Prev / Next */}
      {safe.length > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); prev(); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 bg-white/5 hover:bg-[#C9A055] hover:text-black border border-white/20 text-[#F4F1EA] flex items-center justify-center z-10"
            aria-label="Previous image"
            data-testid="lightbox-prev"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); next(); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 bg-white/5 hover:bg-[#C9A055] hover:text-black border border-white/20 text-[#F4F1EA] flex items-center justify-center z-10"
            aria-label="Next image"
            data-testid="lightbox-next"
          >
            <ChevronRight size={18} />
          </button>
        </>
      )}

      {/* Image — click toggles zoom; click background closes */}
      <div className={`relative max-w-[95vw] max-h-[92vh] ${zoomed ? "overflow-auto" : "overflow-hidden"}`} onClick={(e) => e.stopPropagation()}>
        <img
          src={src}
          alt={alt}
          onClick={() => setZoomed(z => !z)}
          className={`select-none transition-transform duration-300 cursor-${zoomed ? "zoom-out" : "zoom-in"} ${zoomed ? "max-w-none max-h-none scale-[1.6]" : "max-w-[95vw] max-h-[92vh] object-contain"}`}
          draggable={false}
        />
      </div>

      {/* Counter */}
      {safe.length > 1 && (
        <div className="absolute bottom-5 left-1/2 -translate-x-1/2 font-ui text-[10px] tracking-[0.32em] text-[#C9A055]" data-testid="lightbox-counter">
          {idx + 1} / {safe.length}
        </div>
      )}

      {wallOpen && (
        <WallView imageSrc={safe[idx]} alt={alt} onClose={() => setWallOpen(false)} />
      )}
    </div>
  );
}
